import { useState } from "react";
import {
  FaInstagram,
  FaBars,
  FaTimes,
  FaYoutube,
  FaTelegram,
  FaUserCircle
} from "react-icons/fa";
import { MdEmail, MdPhone } from "react-icons/md";
import { IoIosArrowDown } from "react-icons/io";
import { HashLink } from "react-router-hash-link";
import { useLocation, NavLink } from "react-router-dom";
import navLogo from "../assets/navbar-logo.png";
import { motion, AnimatePresence } from "framer-motion";

const navItems = [
  { label: "Home", path: "/" },
  { label: "Courses", path: "/courses", isNew: true },
  {
    label: "About UPSC",
    children: [
      { label: "Exam Pattern", path: "/about-upsc#exampattern" },
      { label: "Book List", path: "/about-upsc#booklist" },
      { label: "Annual Calendar", path: "/about-upsc#annualcalendar" },
      { label: "Common Myth", path: "/about-upsc#commonmyth" },
      { label: "FAQ", path: "/about-upsc#faq" },
    ],
  },
  {
    label: "Syllabus",
    children: [
      { label: "Preliminary", path: "/syllabus#preliminary" },
      { label: "Mains", path: "/syllabus#mains" },
      { label: "Optional", path: "/syllabus#OptionalSubject" },
    ],
  },
  {
    label: "PYQ",
    children: [
      { label: "Prelims", path: "/pyq#prelims" },
      { label: "Mains", path: "/pyq#mains" },
      { label: "Optional", path: "/pyq#optional" },
    ],
  },
  {
    label: "Test Series",
    isNew: true,
    children: [
      { label: "Prelims (GS+CSAT)", path: "/testseries#prelims-series" },
      { label: "Mains General Studies", path: "/testseries#main-gs-series" },
      {
        label: "Anthropology Optional",
        path: "/testseries#anthropology-series",
      },
    ],
  },
  {
    label: "Current Affairs",
    isNew: true,
    children: [
      { label: "Daily", path: "/current#daily" },
      { label: "Monthly", path: "/current#monthly" },
    ],
  },
  { label: "DAW", path: "/daw" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [hoverTimeouts, setHoverTimeouts] = useState({});

  const location = useLocation();

  const handleDummyLogin = (e) => {
    e.preventDefault();
    alert("The Student Login portal is currently undergoing scheduled maintenance. Please try again later.");
  };

  const handleMouseEnter = (labelPath) => {
    if (hoverTimeouts[labelPath]) clearTimeout(hoverTimeouts[labelPath]);
    setOpenDropdowns((prev) => ({ ...prev, [labelPath]: true }));
  };

  const handleMouseLeave = (labelPath) => {
    const timeout = setTimeout(() => {
      setOpenDropdowns((prev) => ({ ...prev, [labelPath]: false }));
    }, 100);
    setHoverTimeouts((prev) => ({ ...prev, [labelPath]: timeout }));
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const scrollWithOffset = (el) => {
    const yOffset = -120;
    const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const renderDropdown = (items, parentPath = "") => (
    <AnimatePresence>
      <motion.ul 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.2 }}
        className="absolute left-0 top-full mt-4 bg-white/90 backdrop-blur-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] border border-white/50 rounded-2xl min-w-[220px] z-50 p-2"
      >
        {items.map((item) => {
          const currentPath = parentPath + item.label;
          const isOpen = !!openDropdowns[currentPath];

          return (
            <li
              key={item.label}
              className="relative group"
              onMouseEnter={() => handleMouseEnter(currentPath)}
              onMouseLeave={() => handleMouseLeave(currentPath)}
            >
              {item.children ? (
                <>
                  <button className="flex justify-between items-center w-full px-4 py-2.5 text-sm font-medium rounded-xl hover:bg-slate-50 hover:text-blue-700 transition-colors">
                    {item.label} <IoIosArrowDown size={14} className="opacity-50" />
                  </button>
                  {isOpen && renderDropdown(item.children, currentPath)}
                </>
              ) : (
                <HashLink
                  smooth
                  to={item.path}
                  scroll={scrollWithOffset}
                  className={`block px-4 py-2.5 text-sm font-medium rounded-xl transition-all ${
                    location.pathname + location.hash === item.path
                      ? "bg-blue-50 text-blue-700 font-bold"
                      : "text-slate-600 hover:bg-slate-50 hover:text-blue-700 hover:pl-5"
                  }`}
                  onClick={() => {
                    setMobileOpen(false);
                    scrollToTop();
                  }}
                >
                  {item.label}
                </HashLink>
              )}
            </li>
          );
        })}
      </motion.ul>
    </AnimatePresence>
  );

  return (
    <header className="w-full sticky top-0 z-50">
      {/* Top Contact Bar */}
      <div className="bg-[#7b0000] text-white/90 text-sm flex justify-between items-center px-4 md:px-8 py-2.5 shadow-inner relative z-20">
        <div className="flex items-center gap-4 md:gap-8 text-xs md:text-sm font-medium tracking-wide">
          <a
            href="mailto:selvasiasacademy@gmail.com"
            className="flex items-center gap-2 hover:text-white transition-colors"
          >
            <MdEmail size={16} /> <span className="hidden sm:inline">selvasiasacademy@gmail.com</span>
          </a>
          <div className="flex md:gap-6 max-md:flex-col max-md:justify-end max-md:items-end">
            <a
              href="tel:+918012003040"
              className="flex items-center gap-2 hover:text-white transition-colors"
            >
              <MdPhone size={16} /> +91 8012003040
            </a>
            <a
              href="tel:+917449203040"
              className="flex items-center gap-2 hover:text-white transition-colors max-sm:hidden"
            >
              <MdPhone size={16} /> +91 7449203040
            </a>
          </div>
        </div>

        <div className="flex items-center gap-4 md:gap-5">
          <NavLink
            to="/courses"
            className="max-md:hidden bg-white/20 hover:bg-white/30 text-white font-bold px-4 py-1.5 rounded-full transition-all text-xs uppercase tracking-wider backdrop-blur-sm border border-white/10 shadow-sm flex items-center"
          >
            Apply Now
          </NavLink>
          <a
            href="https://www.instagram.com/selvas_ias_academy?igsh=dW83enprOGRtMWR3"
            className="hover:text-white hover:scale-110 transition-all"
          >
            <FaInstagram size={16} />
          </a>
          <a
            href="https://youtube.com/@selvasiasacademy?si=fz9ykMgZpap6mCn6"
            className="hover:text-white hover:scale-110 transition-all"
          >
            <FaYoutube size={16} />
          </a>
          <a href="https://t.me/Selvasanthro" className="hover:text-white hover:scale-110 transition-all">
            <FaTelegram size={16} />
          </a>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className="relative z-10 w-full flex justify-between items-center px-4 md:px-8 py-4 bg-white/80 backdrop-blur-2xl shadow-sm border-b border-white/20">
        <HashLink
          to="/"
          onClick={scrollToTop}
          className="flex items-center gap-3 transform transition-transform hover:scale-105"
        >
          <img
            src={navLogo}
            alt="Selva's IAS Academy"
            className="h-10 md:h-12 w-auto"
          />
        </HashLink>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-2 xl:gap-6">
          <ul className="flex items-center gap-1 xl:gap-2 font-medium text-slate-700 text-[15px]">
            {navItems.map((item) => {
              const isOpen = !!openDropdowns[item.label];
              const isActive =
                location.pathname + location.hash === item.path ||
                (item.children &&
                  item.children.some(
                    (child) => location.pathname + location.hash === child.path
                  ));
              return (
                <li
                  key={item.label}
                  className="relative group flex items-center"
                  onMouseEnter={() => handleMouseEnter(item.label)}
                  onMouseLeave={() => handleMouseLeave(item.label)}
                >
                  {item.children ? (
                    <>
                      <span
                        className={`flex items-center gap-1.5 cursor-pointer px-3 py-2 rounded-xl transition-colors ${
                          isActive || isOpen
                            ? "bg-slate-50 text-blue-800 font-bold"
                            : "hover:bg-slate-50 hover:text-blue-700"
                        }`}
                      >
                        {item.label}
                        {item.isNew && (
                          <span className="ml-1 flex items-center text-[9px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-black uppercase tracking-widest border border-red-200">
                            New
                          </span>
                        )}
                        <IoIosArrowDown size={14} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                      </span>
                      {isOpen && renderDropdown(item.children, item.label)}
                    </>
                  ) : (
                    <HashLink
                      smooth
                      to={item.path}
                      scroll={scrollWithOffset}
                      className={`flex items-center px-3 py-2 rounded-xl transition-colors ${
                        isActive
                          ? "bg-slate-50 text-blue-800 font-bold"
                          : "hover:bg-slate-50 hover:text-blue-700"
                      }`}
                      onClick={scrollToTop}
                    >
                      {item.label}
                      {item.isNew && (
                        <span className="ml-1 text-[9px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-black uppercase tracking-widest border border-red-200">
                          New
                        </span>
                      )}
                    </HashLink>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Dummy Login Button */}
          <button 
            onClick={handleDummyLogin}
            className="ml-4 flex items-center gap-2 px-6 py-2.5 rounded-full bg-slate-900 text-white font-semibold text-sm hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-700/30 transition-all duration-300"
          >
            <FaUserCircle size={18} />
            Login
          </button>
        </div>

        {/* Mobile Menu Toggle */}
        <div className="flex items-center gap-3 lg:hidden">
          <button 
            onClick={handleDummyLogin}
            className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
          >
            <FaUserCircle size={20} />
          </button>
          
          <button
            className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-50 text-slate-800 border border-slate-200 hover:bg-slate-100 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <FaTimes size={18} /> : <FaBars size={18} />}
          </button>
        </div>

        {/* Mobile Nav */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="absolute top-full left-0 w-full bg-white/95 backdrop-blur-3xl shadow-[0_20px_40px_-15px_rgba(0,0,0,0.1)] lg:hidden z-50 border-t border-slate-100 overflow-hidden"
            >
              <ul className="flex flex-col p-6 gap-2 font-medium text-slate-700 max-h-[70vh] overflow-y-auto">
                {navItems.map((item) =>
                  item.children ? (
                    <details key={item.label} className="group bg-slate-50 rounded-2xl mb-2">
                      <summary className="flex justify-between items-center cursor-pointer p-4 hover:text-blue-700 rounded-2xl font-bold">
                        <span className="flex items-center gap-2">
                          {item.label}
                          {item.isNew && (
                            <span className="ml-2 flex items-center text-[9px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-black uppercase tracking-widest border border-red-200">
                              New
                            </span>
                          )}
                        </span>
                        <IoIosArrowDown className="inline transition-transform group-open:rotate-180" />
                      </summary>
                      <ul className="px-4 pb-4 flex flex-col gap-1">
                        {item.children.map((child) => (
                          <li key={child.label}>
                            <HashLink
                              smooth
                              to={child.path || "#"}
                              scroll={scrollWithOffset}
                              className={`block p-3 rounded-xl transition-colors ${
                                location.pathname + location.hash === child.path
                                  ? "bg-white text-blue-700 font-bold shadow-sm"
                                  : "hover:bg-white hover:text-blue-700"
                              }`}
                              onClick={() =>
                                setMobileOpen(false) || scrollToTop()
                              }
                            >
                              {child.label}
                            </HashLink>
                          </li>
                        ))}
                      </ul>
                    </details>
                  ) : (
                    <HashLink
                      key={item.label}
                      smooth
                      to={item.path}
                      scroll={scrollWithOffset}
                      className={`flex items-center gap-2 p-4 rounded-2xl mb-2 transition-colors ${
                        location.pathname + location.hash === item.path
                          ? "bg-blue-50 text-blue-800 font-bold"
                          : "hover:bg-slate-50 hover:text-blue-700"
                      }`}
                      onClick={() => setMobileOpen(false) || scrollToTop()}
                    >
                      {item.label}
                      {item.isNew && (
                        <span className="ml-2 flex items-center text-[9px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-black uppercase tracking-widest border border-red-200">
                          New
                        </span>
                      )}
                    </HashLink>
                  )
                )}
                
                {/* Mobile Login Button inside menu */}
                <button 
                  onClick={handleDummyLogin}
                  className="mt-4 flex justify-center items-center gap-2 w-full p-4 rounded-2xl bg-slate-900 text-white font-bold hover:bg-blue-700 transition-colors"
                >
                  <FaUserCircle size={20} />
                  Student Login
                </button>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
}
